import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import {
  AppLayout,
  TopNavigation,
  TopNavigationProps,
  Tabs,
  Box,
  SpaceBetween,
} from '@cloudscape-design/components';
import { Authenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession, getCurrentUser, signOut as amplifySignOut, signInWithRedirect } from 'aws-amplify/auth';
import { TestScenario } from './components/TestScenario';
import { HistoryView } from './components/HistoryView';
import { AdminPanel } from './components/AdminPanel';
import { AnalyticsDashboard } from './components/analytics/AnalyticsDashboard';
import { AdminLayout } from './components/AdminLayout';
import { JudgePanel } from './components/JudgePanel';
import { TEST_SCENARIOS, API_CONFIG } from './config';
import { webSocketManager } from './services/WebSocketManager';
import { configureAmplify } from './auth/amplifyConfig';
import '@cloudscape-design/global-styles/index.css';
import '@aws-amplify/ui-react/styles.css';

// Configure Amplify
configureAmplify();

const hasSSOConfig = !!API_CONFIG.COGNITO_DOMAIN;

function App() {
  if (hasSSOConfig) {
    return <SSOApp />;
  }
  return (
    <Authenticator>
      {({ signOut, user }) => <AuthenticatedApp signOut={signOut} user={user} />}
    </Authenticator>
  );
}

function SSOApp() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      await getCurrentUser();
      setIsAuthenticated(true);
    } catch {
      // Not authenticated — redirect to SSO
      signInWithRedirect({ provider: { custom: 'AmazonFederate' } });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || !isAuthenticated) {
    return (
      <Box textAlign="center" padding="xxl">
        <Box variant="p">Redirecting to SSO...</Box>
      </Box>
    );
  }

  const handleSignOut = async () => {
    await amplifySignOut();
  };

  return <AuthenticatedApp signOut={handleSignOut} user={undefined} />;
}

interface AuthenticatedAppProps {
  signOut?: () => void;
  user?: any;
}

function AuthenticatedApp({ signOut, user }: AuthenticatedAppProps) {
  const [isConnected, setIsConnected] = useState(false);
  const [userId, setUserId] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [displayName, setDisplayName] = useState('');

  useEffect(() => {
    // Get user info and token from Cognito
    const initAuth = async () => {
      try {
        const currentUser = await getCurrentUser();
        setUserId(currentUser.userId);

        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        
        if (idToken) {
          webSocketManager.connect(idToken);
        }

        // Extract display name from token claims
        const claims = session.tokens?.idToken?.payload;
        const name = (claims?.['given_name'] as string)
          || (claims?.email as string)
          || (claims?.['cognito:username'] as string)
          || currentUser.username
          || '';
        setDisplayName(name);

        // Check if user is in admin group or is a federated SSO user
        const groups = session.tokens?.accessToken?.payload['cognito:groups'] as string[] | undefined;
        const isAdminUser = groups?.includes('admin') || groups?.some(g => g.includes('AmazonFederate')) || false;
        setIsAdmin(isAdminUser);
      } catch (error) {
        console.error('Error initializing auth:', error);
      }
    };

    initAuth();

    // Subscribe to connection state changes
    const unsubscribe = webSocketManager.onConnectionStateChange(setIsConnected);

    return () => {
      unsubscribe();
      webSocketManager.disconnect();
    };
  }, []);

  const utilities: TopNavigationProps.Utility[] = [
    {
      type: 'button',
      text: isConnected ? 'Connected' : 'Disconnected',
      variant: 'primary-button',
      disableTextCollapse: true,
      disableUtilityCollapse: true,
    },
  ];

  if (isAdmin) {
    utilities.push({
      type: 'button',
      text: 'Admin',
      href: '/admin',
    });
  }

  utilities.push({
    type: 'menu-dropdown',
    text: displayName || 'User',
    iconName: 'user-profile',
    items: [
      { id: 'signout', text: 'Sign Out' },
    ],
    onItemClick: ({ detail }) => {
      if (detail.id === 'signout') {
        signOut?.();
      }
    },
  });

  return (
    <Router>
      <TopNavigation
        identity={{
          href: '/',
          title: 'LLM Bench',
        }}
        utilities={utilities}
      />
      <AppLayout
        navigationHide
        toolsHide
        content={
          <Routes>
            <Route path="/" element={<TestScenariosPage userId={userId} />} />
            <Route path="/:slug" element={<TestScenariosPage userId={userId} />} />
            <Route path="/history" element={<HistoryView userId={userId} />} />
            {isAdmin && (
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminPanel />} />
                <Route path="analytics" element={<AnalyticsDashboard />} />
                <Route path="judge" element={<JudgePanel />} />
              </Route>
            )}
          </Routes>
        }
      />
    </Router>
  );
}

interface TestScenariosPageProps {
  userId: string;
}

const TestScenariosPage: React.FC<TestScenariosPageProps> = ({ userId }) => {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();

  // Resolve active tab from URL slug
  const matchedScenario = slug ? TEST_SCENARIOS.find((s) => s.slug === slug) : null;
  const activeTabId = matchedScenario ? `test${matchedScenario.id}` : 'test1';

  const tabs = TEST_SCENARIOS.map(scenario => ({
    id: `test${scenario.id}`,
    label: scenario.name,
    href: `/${scenario.slug}`,
    content: (
      <Box padding={{ top: 'l' }}>
        <TestScenario
          testId={scenario.id}
          testName={scenario.name}
          testPrompt={scenario.prompt}
          testDescription={scenario.description}
          userId={userId}
          webSearchDefault={scenario.webSearchDefault}
        />
      </Box>
    ),
  }));

  return (
    <SpaceBetween size="l">
      <Box padding="l">
        <Tabs
          tabs={tabs}
          activeTabId={activeTabId}
          onChange={({ detail }) => {
            const scenario = TEST_SCENARIOS.find((s) => `test${s.id}` === detail.activeTabId);
            if (scenario) navigate(`/${scenario.slug}`);
          }}
        />
      </Box>
    </SpaceBetween>
  );
};

export default App;
